import {
  CreatePurchaseRequestItemV2Payload,
  CreatePurchaseRequestV2Payload,
  PurchaseRequestFilePayload,
  PurchaseRequestItemState,
} from "../../../types";
import type { ItemRow } from "./types";

/**
 * Assembly of the `POST /api/purchase-requests` create body. Extracted verbatim from the
 * pre-Carbon `NewPurchaseRequestV2` page's own `handleSubmit` so the exact wire shape is preserved
 * across the Carbon/RHF migration and can be diffed field-for-field against what
 * `useCreatePurchaseRequestV2` sends. NOTHING about the produced payload changed in the migration —
 * only where its inputs are read from (RHF form values instead of `useState`).
 */

/** Fields the row carries only for display; the API rejects nothing but has no use for them, so
 *  they are dropped before the request is sent. */
const DISPLAY_ONLY_KEYS = [
  "clientId",
  "materialEan",
  "materialName",
  "materialOwnerName",
] as const;

export const isUUID = (value?: string) =>
  !!value &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const stripDisplayFields = (
  item: ItemRow
): CreatePurchaseRequestItemV2Payload => {
  const payload: Record<string, unknown> = { ...item };
  DISPLAY_ONLY_KEYS.forEach((key) => delete payload[key]);
  return payload as CreatePurchaseRequestItemV2Payload;
};

const normalizeFile = (
  file: PurchaseRequestFilePayload
): PurchaseRequestFilePayload => {
  const payload: PurchaseRequestFilePayload = {
    fileURL: file.fileURL,
    name: file.name,
    type: file.type,
  };
  if (file.id && isUUID(file.id)) payload.id = file.id;
  return payload;
};

export interface RequestPayloadInput {
  name: string;
  purchaseRequestCode: string;
  comments: string;
  files: PurchaseRequestFilePayload[];
  items: ItemRow[];
}

export const buildCreatePayload = (
  input: RequestPayloadInput,
  draft: boolean
): CreatePurchaseRequestV2Payload => {
  const payloadItems: CreatePurchaseRequestItemV2Payload[] = input.items.map(
    (item) => {
      const rest = stripDisplayFields(item);
      const itemFiles = rest.files?.map(normalizeFile);
      return {
        ...rest,
        files: itemFiles && itemFiles.length ? itemFiles : undefined,
        budgetCurrency: rest.currency ?? null,
        state: draft
          ? PurchaseRequestItemState.DRAFT
          : PurchaseRequestItemState.IN_APPROVAL,
      };
    }
  );

  const normalizedFiles = input.files.map(normalizeFile);

  return {
    name: input.name || null,
    purchaseRequestCode: input.purchaseRequestCode.trim() || null,
    comments: input.comments || null,
    originChannel: "MANUAL",
    draft,
    files: normalizedFiles.length ? normalizedFiles : undefined,
    items: payloadItems,
  };
};
